import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TotpEnrollment, TotpSetupStep } from '../../profile-security.models';

@Component({
  selector: 'app-profile-two-factor-section',
  templateUrl: './two-factor-section.component.html',
  styleUrl: './two-factor-section.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorSectionComponent implements OnChanges {
  @Input() totpEnabled = false;
  @Input() loadingStatus = false;
  @Input() startingEnrollment = false;
  @Input() confirmingEnrollment = false;
  @Input() disablingTotp = false;
  @Input() showSetupDialog = false;
  @Input() setupStep: TotpSetupStep = 'password';
  @Input() enrollment: TotpEnrollment | null = null;
  @Input({ required: true }) totpPasswordForm!: FormGroup;
  @Input({ required: true }) totpVerifyForm!: FormGroup;
  @Input() showDisableDialog = false;
  @Input({ required: true }) totpDisableForm!: FormGroup;

  @Output() openSetupDialog = new EventEmitter<void>();
  @Output() closeSetupDialog = new EventEmitter<void>();
  @Output() submitEnrollmentPassword = new EventEmitter<void>();
  @Output() submitEnrollmentCode = new EventEmitter<void>();
  @Output() openDisableDialog = new EventEmitter<void>();
  @Output() closeDisableDialog = new EventEmitter<void>();
  @Output() submitDisableTotp = new EventEmitter<void>();
  @Output() copySecret = new EventEmitter<string>();
  @Output() setupStepChange = new EventEmitter<TotpSetupStep>();

  qrDataUrl: string | null = null;

  /** Orden de las etapas del diálogo; el índice alimenta al p-stepper (base 1). */
  private readonly setupSteps: TotpSetupStep[] = ['password', 'qr', 'verify'];

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  get setupStepIndex(): number {
    return this.setupSteps.indexOf(this.setupStep) + 1;
  }

  goToStep(step: TotpSetupStep): void {
    if (step !== this.setupStep) this.setupStepChange.emit(step);
  }

  onStepperValueChange(value: number | undefined): void {
    const step = this.setupSteps[(value ?? 1) - 1];
    if (step) this.goToStep(step);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['enrollment']) {
      void this.renderQrCode();
    }
  }

  get secret(): string {
    return this.enrollment?.secret || '';
  }

  onCopySecret(): void {
    if (this.secret) this.copySecret.emit(this.secret);
  }

  /**
   * El QR se genera en el navegador para que el secreto TOTP no se materialice
   * en ningún artefacto adicional fuera de la respuesta de la API.
   */
  async renderQrCode(): Promise<void> {
    const uri = this.enrollment?.otpauthUri;
    if (!uri) {
      this.qrDataUrl = null;
      return;
    }

    try {
      const qrcodeModule = await import('qrcode-generator');
      const qrcode = (qrcodeModule as any).default ?? (qrcodeModule as any);
      if (typeof qrcode !== 'function') {
        throw new Error('qrcode-generator export not found');
      }
      const qr = qrcode(0, 'M');
      qr.addData(uri);
      qr.make();
      this.qrDataUrl = qr.createDataURL(6, 2);
    } catch {
      this.qrDataUrl = null;
    } finally {
      this.changeDetectorRef.markForCheck();
    }
  }
}
